import React from 'react';
import {List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography} from '@mui/material';
import {useNavigate} from 'react-router';
import {useSession} from '../../../../firebase/UserProvider';
import {useUserDisclosures} from '../../../../features/products/hooks/useUserDisclosures';
import {useDisclosuresForReview} from './hooks/useDisclosuresForReview';
import ReportsTable from '../../../reviewer/pages/ListOfReports/components/ReportsTable';
import {TABLE_COLUMNS} from '../../../reviewer/pages/ListOfReports/constants';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

/**
 * Disclosure Officer page: shows a list of disclosure-type products.
 * Uses realtime Firestore subscription via useUserDisclosures.
 */
const ListOfDisclosures = () => {
    const navigate = useNavigate();
    const {user} = useSession();

    const [view, setView] = React.useState('review'); // 'review' | 'approved'

    const review = useDisclosuresForReview(user?.uid);
    const approved = useUserDisclosures(user?.uid);

    const {reports, loading} = view === 'review' ? review : approved;

    const handleRowClick = (reportId) => {
        navigate(`/disclosures/${reportId}`);
    };

    const SIDEBAR_ITEMS = [
        {
            key: 'review',
            label: 'Review',
            icon: <RateReviewIcon/>,
            description: 'Awaiting review'
        },
        {
            key: 'approved',
            label: 'Approved',
            icon: <CheckCircleIcon/>,
            description: 'Approved disclosures'
        }
    ];

    const isActive = (key) => view === key;

    return (
        <Stack direction="row" height="100%">
            {/* Left panel (ProductsSidebar style) */}
            <Stack
                sx={{
                    width: 240,
                    minWidth: 240,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    height: '100%'
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        px: 2,
                        py: 1.5,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.75rem'
                    }}
                >
                    Disclosures
                </Typography>

                <List disablePadding>
                    {SIDEBAR_ITEMS.map((item) => (
                        <ListItemButton
                            key={item.key}
                            selected={isActive(item.key)}
                            onClick={() => setView(item.key)}
                            sx={{
                                py: 1.5,
                                '&.Mui-selected': {
                                    bgcolor: 'action.selected',
                                    borderRight: '3px solid',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'action.selected',
                                    }
                                }
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 40}}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                secondary={item.description}
                                primaryTypographyProps={{
                                    variant: 'body2',
                                    fontWeight: isActive(item.key) ? 600 : 400
                                }}
                                secondaryTypographyProps={{
                                    variant: 'caption',
                                    sx: {opacity: 0.7}
                                }}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Stack>

            {/* Main content */}
            <Stack p={2} spacing={3} flex={1} overflow="auto">
                <Typography variant="h5">
                    {reports?.length || 0} {view === 'review' ? 'Disclosures to review' : 'Approved Disclosures'}
                </Typography>

                <ReportsTable
                    reports={reports}
                    columns={TABLE_COLUMNS}
                    loading={loading}
                    onRowClick={handleRowClick}
                />
            </Stack>
        </Stack>
    );
};

export default ListOfDisclosures;
